import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import dayjs from "dayjs";
import { CalendarDays, Clock, User, CheckCircle2, ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ProPractBadge } from "./ProPractBadge";
import { TIME_SLOTS } from "./data";
import { useCatalog, useSite } from "@/lib/site";
import { api, formatApiError } from "@/lib/api";
import { loadRazorpay, openRazorpayCheckout } from "@/lib/razorpay";

const STEPS = [
  { icon: CalendarDays, label: "Service" },
  { icon: Clock, label: "Slot" },
  { icon: User, label: "Details" },
  { icon: CheckCircle2, label: "Confirm" },
];

export const BookingModal = ({ open, onClose, preselectedTitle }) => {
  const { services } = useCatalog();
  const [step, setStep] = useState(0);
  const [service, setService] = useState(preselectedTitle || null);
  const [date, setDate] = useState(null);
  const [slot, setSlot] = useState(null);
  const [slots, setSlots] = useState(null);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [intakeForm, setIntakeForm] = useState(null);
  const [intakeAnswers, setIntakeAnswers] = useState({});
  const [form, setForm] = useState({ name: "", email: "", phone: "", concern: "" });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [paid, setPaid] = useState(false);
  const [payMethod, setPayMethod] = useState("");
  const { content } = useSite();
  const whatsapp = (content.settings?.whatsapp_number || "").replace(/[^0-9]/g, "");

  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => dayjs().add(i + 1, "day")), []);
  const chosen = services.find((s) => s.title === service);
  const live = !!chosen?.service_id;
  const paymentOptions = live
    ? [
        chosen.allow_online_payment && { id: "razorpay", label: "Pay online now", sub: "UPI, cards & netbanking" },
        chosen.allow_direct_payment && { id: "direct", label: "Pay directly", sub: "UPI / bank transfer — details below" },
        chosen.allow_pay_in_person && { id: "in_person", label: "Pay in person", sub: "At the clinic on the day of the session" },
      ].filter(Boolean)
    : [];
  const directDetails = chosen?.direct_payment_details || {};

  const loadSlots = async (d, svc = chosen) => {
    if (!svc?.service_id) return;
    setSlotsLoading(true);
    setSlots(null);
    setSlot(null);
    try {
      const r = await api.get("/propract/booking/availability", {
        params: { date: d.format("YYYY-MM-DD"), service_id: svc.service_id },
      });
      const all = r.data?.available_slots || [];
      setSlots(all.filter((sl) => !sl.service_id || sl.service_id === svc.service_id));
    } catch {
      setSlots([]);
      toast.error("Could not load slots — please try another date");
    } finally {
      setSlotsLoading(false);
    }
  };

  const loadIntake = async () => {
    try {
      const r = await api.get("/propract/booking/intake-form");
      if (r.data?.enabled && r.data?.fields?.length) setIntakeForm(r.data);
    } catch {
      // intake optional
    }
  };

  const pickDate = (d) => {
    setDate(d);
    if (live) loadSlots(d);
  };

  const reset = () => {
    setStep(0);
    setService(preselectedTitle || null);
    setDate(null);
    setSlot(null);
    setSlots(null);
    setIntakeForm(null);
    setIntakeAnswers({});
    setForm({ name: "", email: "", phone: "", concern: "" });
    setResult(null);
    setPayMethod("");
    setPaid(false);
  };

  const close = () => {
    onClose();
    setTimeout(reset, 300);
  };

  const canNext =
    (step === 0 && service) ||
    (step === 1 && date && slot) ||
    (step === 2 &&
      form.name.trim() &&
      form.phone.trim() &&
      (!live || !intakeForm || intakeForm.fields.every((f) => !f.required || (intakeAnswers[f.field_id] || "").trim())));

  const verifyBooking = async (resp, bookingResult) => {
    try {
      await api.post("/propract/booking/verify-payment", {
        ...resp,
        booking_id: bookingResult.booking_id || bookingResult.id,
      });
      setPaid(true);
      window.__tblTrack?.("payment_success", { service: chosen?.title || "" });
    } catch {
      toast.error("Payment received but verification failed — contact support with your payment ID.");
    }
  };

  const openBookingPayment = (bookingResult) => {
    window.__tblTrack?.("payment_started", { service: chosen?.title || "" });
    return openRazorpayCheckout({
      data: bookingResult,
      description: chosen?.title || "Consultation",
      prefill: { name: form.name, email: form.email, contact: form.phone },
      onSuccess: (resp) => verifyBooking(resp, bookingResult),
    });
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      if (live) {
        const responses = Object.fromEntries(Object.entries(intakeAnswers).filter(([, v]) => (v || "").trim()));
        const r = await api.post("/propract/booking/create", {
          service_id: chosen.service_id,
          date: date.format("YYYY-MM-DD"),
          time: slot.time,
          mode: chosen.mode_raw || slot.mode || "online",
          payment_method: payMethod || undefined,
          client_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          client_info: {
            name: form.name,
            email: form.email,
            phone: form.phone,
            notes: form.concern || undefined,
            intake_form_responses: Object.keys(responses).length ? responses : undefined,
          },
        });
        setResult(r.data);
        window.__tblTrack?.("booking_created", { service: chosen?.title || "", mode: chosen?.mode_raw || "online", payment: payMethod || "later" });
        if (payMethod === "razorpay" && r.data.razorpay_order_id) {
          await loadRazorpay();
          openBookingPayment(r.data);
        }
      } else {
        await api.post("/enquiries", {
          name: form.name,
          phone: form.phone,
          concern: form.concern,
          service: chosen?.title || "Consultation",
          date: date?.format("YYYY-MM-DD"),
          slot: slot?.time || slot,
        });
        setResult({ demo: true });
        window.__tblTrack?.("enquiry_created", { service: chosen?.title || "" });
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(formatApiError(detail?.upstream?.detail || detail?.message || detail));
      setSubmitting(false);
      return;
    }
    setSubmitting(false);
  };

  const done = !!result;
  const bookingNeedsPayment =
    done && !result.demo && payMethod === "razorpay" && !!result.razorpay_order_id && !paid;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && close()}>
      <DialogContent data-testid="booking-modal" className="flex max-h-[90vh] max-w-lg flex-col rounded-3xl border-ink/10 bg-sand p-0 overflow-hidden">
        <div className="shrink-0 bg-ink px-7 py-5 text-sand">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between font-display text-2xl font-medium">
              Book a consultation
              <ProPractBadge href="https://propract.com" />
            </DialogTitle>
          </DialogHeader>
          {!done && (
            <div className="mt-4 flex items-center gap-2" data-testid="booking-steps">
              {STEPS.map((s, i) => (
                <div key={s.label} className="flex flex-1 items-center gap-2">
                  <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-outfit font-semibold transition-colors duration-300 ${i <= step ? "bg-gold text-ink" : "bg-sand/15 text-sand/60"}`}>
                    {i + 1}
                  </span>
                  <span className={`hidden text-xs font-outfit sm:block ${i <= step ? "text-sand" : "text-sand/50"}`}>{s.label}</span>
                  {i < STEPS.length - 1 && <span className="h-px flex-1 bg-sand/15" />}
                </div>
              ))}
            </div>
          )}
        </div>

        <div data-lenis-prevent className="flex-1 overflow-y-auto overscroll-contain px-7 py-6">
          <AnimatePresence mode="wait">
            {done ? (
              <motion.div key="done" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="py-8 text-center" data-testid="booking-success">
                <CheckCircle2 className={`mx-auto h-14 w-14 ${bookingNeedsPayment ? "text-terra" : "text-sage"}`} />
                <h3 className="mt-4 font-display text-2xl font-semibold text-ink">
                  {result.demo
                    ? "Request received"
                    : bookingNeedsPayment
                      ? "Payment pending"
                      : paid
                        ? "Payment received — booking confirmed"
                        : "Booking confirmed"}
                </h3>
                <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-plumsoft">
                  {result.demo
                    ? "Your request was saved — the team will confirm your slot on WhatsApp shortly."
                    : bookingNeedsPayment
                      ? `Your slot is reserved. Complete the payment of ${chosen?.price} to confirm the booking.`
                      : result.message || "Your slot is booked. Confirmation and calendar invite are on their way to your WhatsApp & email."}
                </p>
                {!result.demo && (
                  <p className="mt-4 text-xs text-faint" data-testid="booking-propract-credit">
                    Need to reschedule? Reply to your WhatsApp confirmation · Secure bookings powered by{" "}
                    <a href="https://propract.com" target="_blank" rel="noopener" className="font-semibold text-terra hover:underline">
                      ProPract
                    </a>
                  </p>
                )}
                {bookingNeedsPayment && (
                  <button
                    data-testid="booking-pay-now"
                    onClick={() => openBookingPayment(result)}
                    className="mt-5 inline-flex items-center gap-2 rounded-full bg-terra px-6 py-3 font-outfit text-sm font-semibold text-white hover:bg-terradark"
                  >
                    Pay now
                  </button>
                )}
                {!result.demo && payMethod === "direct" && (directDetails.upi_id || directDetails.qr_code_url) && (
                  <div className="mx-auto mt-5 max-w-sm rounded-2xl border border-terra/20 bg-white p-5 text-left" data-testid="booking-payment-instructions">
                    <p className="font-outfit text-xs font-semibold uppercase tracking-widest text-terra">Complete your payment</p>
                    {directDetails.upi_id && (
                      <p className="mt-2 text-sm text-ink">
                        UPI ID: <span className="font-semibold">{directDetails.upi_id}</span>
                      </p>
                    )}
                    {directDetails.qr_code_url && (
                      <img src={directDetails.qr_code_url} alt="UPI QR code" className="mx-auto mt-3 h-32 w-32 rounded-lg border border-ink/10 bg-white object-contain p-1" />
                    )}
                    {directDetails.instructions && <p className="mt-2 text-xs leading-relaxed text-plumsoft">{directDetails.instructions}</p>}
                    {whatsapp && (
                      <a
                        data-testid="booking-payment-whatsapp"
                        href={`https://wa.me/${whatsapp}?text=${encodeURIComponent(`Hi, I've booked ${chosen?.title} on ${date?.format("D MMM")} at ${slot?.time || slot}. Sharing my payment screenshot.`)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-[#25D366] px-4 py-2 font-outfit text-xs font-semibold text-white"
                      >
                        Share payment screenshot on WhatsApp
                      </a>
                    )}
                  </div>
                )}
                {(result.booking_id || result.id) && (
                  <p className="mt-3 font-outfit text-xs text-faint" data-testid="booking-reference">
                    Reference: {result.booking_id || result.id}
                  </p>
                )}
                <button data-testid="booking-done-close" onClick={close} className="mt-6 rounded-full bg-ink px-6 py-3 font-outfit text-sm font-semibold text-sand hover:bg-terra transition-colors duration-300">
                  Done
                </button>
              </motion.div>
            ) : (
              <motion.div key={step} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.3 }}>
                {step === 0 && (
                  <div className="space-y-3" data-testid="booking-step-service">
                    {services.map((s, i) => (
                      <button
                        key={s.id || s.service_id || i}
                        data-testid={`booking-service-${i}`}
                        onClick={() => setService(s.title)}
                        className={`w-full rounded-2xl border p-4 text-left transition-all duration-300 ${
                          service === s.title ? "border-terra bg-terra/5 shadow-lift" : "border-ink/10 bg-white hover:border-terra/40"
                        }`}
                      >
                        <p className="font-outfit text-sm font-semibold text-ink">{s.title}</p>
                        <p className="mt-1 text-xs text-faint">{[s.duration, s.price, s.mode].filter(Boolean).join(" · ")}</p>
                      </button>
                    ))}
                  </div>
                )}

                {step === 1 && (
                  <div data-testid="booking-step-slot">
                    <p className="mb-3 font-outfit text-xs font-semibold uppercase tracking-widest text-faint">Choose a date</p>
                    <div className="grid grid-cols-4 gap-2 sm:grid-cols-7">
                      {days.map((d) => (
                        <button
                          key={d.format("YYYY-MM-DD")}
                          data-testid={`booking-date-${d.format("YYYY-MM-DD")}`}
                          onClick={() => pickDate(d)}
                          className={`rounded-xl border px-1 py-2.5 text-center transition-all duration-200 ${
                            date?.isSame(d, "day") ? "border-terra bg-terra text-white" : "border-ink/10 bg-white hover:border-terra/40"
                          }`}
                        >
                          <span className="block text-[10px] font-outfit uppercase">{d.format("ddd")}</span>
                          <span className="block font-display text-lg font-semibold">{d.format("D")}</span>
                        </button>
                      ))}
                    </div>
                    <p className="mb-3 mt-6 font-outfit text-xs font-semibold uppercase tracking-widest text-faint">
                      {live ? "Live availability (IST)" : "Choose a time (IST)"}
                    </p>
                    {live && !date && <p className="text-xs text-faint">Pick a date to see real-time slots.</p>}
                    {live && slotsLoading && (
                      <p className="flex items-center gap-2 text-sm text-faint" data-testid="booking-slots-loading">
                        <Loader2 className="h-4 w-4 animate-spin" /> Checking availability…
                      </p>
                    )}
                    {live && slots && slots.length > 0 && slots.every((sl) => sl.bookable === false) && !slotsLoading && (
                      <p className="mb-3 rounded-xl bg-terra/10 px-4 py-3 text-xs leading-relaxed text-terra" data-testid="booking-slots-unavailable-reason">
                        {slots[0].reason || "This service isn't available on this date — try another day."}
                      </p>
                    )}
                    {live && slots && slots.length === 0 && !slotsLoading && (
                      <p className="text-sm text-faint" data-testid="booking-no-slots">No slots free on this date — try another day.</p>
                    )}
                    <div className="grid grid-cols-3 gap-2">
                      {(live ? slots || [] : TIME_SLOTS.map((t) => ({ time: t }))).map((t) => {
                        const disabled = live && t.bookable === false;
                        return (
                          <button
                            key={t.time || t}
                            data-testid={`booking-slot-${(t.time || t).replace(":", "")}`}
                            onClick={() => {
                              if (disabled) return;
                              setSlot(t);
                              window.__tblTrack?.("booking_slot_selected", { service: service || "", date: date?.format("YYYY-MM-DD") || "" });
                            }}
                            disabled={disabled}
                            title={t.reason || ""}
                            className={`rounded-xl border py-2.5 font-outfit text-sm font-medium transition-all duration-200 ${
                              disabled
                                ? "cursor-not-allowed border-ink/5 bg-ink/[0.03] text-faint/50 line-through"
                                : slot && (slot.time || slot) === (t.time || t)
                                  ? "border-terra bg-terra text-white"
                                  : "border-ink/10 bg-white hover:border-terra/40"
                            }`}
                          >
                            {t.time || t}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {step === 2 && (
                  <div className="space-y-4" data-testid="booking-step-details">
                    <input data-testid="booking-input-name" placeholder="Mother's full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full rounded-xl border border-ink/15 bg-white px-4 py-3 text-sm outline-none transition-colors focus:border-terra" />
                    <input data-testid="booking-input-email" type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full rounded-xl border border-ink/15 bg-white px-4 py-3 text-sm outline-none transition-colors focus:border-terra" />
                    <input data-testid="booking-input-phone" placeholder="WhatsApp number" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full rounded-xl border border-ink/15 bg-white px-4 py-3 text-sm outline-none transition-colors focus:border-terra" />
                    <textarea data-testid="booking-input-concern" placeholder="Briefly share your concern (optional)" rows={2} value={form.concern} onChange={(e) => setForm({ ...form, concern: e.target.value })} className="w-full rounded-xl border border-ink/15 bg-white px-4 py-3 text-sm outline-none transition-colors focus:border-terra" />
                    {live && intakeForm && (
                      <div className="rounded-2xl border border-terra/20 bg-white p-4" data-testid="booking-intake-form">
                        <p className="mb-3 font-outfit text-xs font-semibold uppercase tracking-widest text-terra">{intakeForm.title || "Intake form"}</p>
                        <div className="space-y-3">
                          {intakeForm.fields.map((f) => (
                            <div key={f.field_id}>
                              <label className="mb-1 block text-xs font-medium text-plumsoft">
                                {f.label} {f.required && <span className="text-terra">*</span>}
                              </label>
                              {f.field_type === "textarea" ? (
                                <textarea
                                  data-testid={`booking-intake-${f.field_id}`}
                                  rows={2}
                                  value={intakeAnswers[f.field_id] || ""}
                                  onChange={(e) => setIntakeAnswers({ ...intakeAnswers, [f.field_id]: e.target.value })}
                                  className="w-full rounded-xl border border-ink/15 px-3 py-2 text-sm outline-none focus:border-terra"
                                />
                              ) : (
                                <input
                                  data-testid={`booking-intake-${f.field_id}`}
                                  value={intakeAnswers[f.field_id] || ""}
                                  onChange={(e) => setIntakeAnswers({ ...intakeAnswers, [f.field_id]: e.target.value })}
                                  className="w-full rounded-xl border border-ink/15 px-3 py-2 text-sm outline-none focus:border-terra"
                                />
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {step === 3 && (
                  <div data-testid="booking-step-summary" className="rounded-2xl border border-ink/10 bg-white p-6">
                    <h4 className="font-display text-xl font-semibold text-ink">Summary</h4>
                    <dl className="mt-4 space-y-2 text-sm">
                      <div className="flex justify-between"><dt className="text-faint">Service</dt><dd className="font-medium text-ink">{chosen?.title}</dd></div>
                      <div className="flex justify-between"><dt className="text-faint">When</dt><dd className="font-medium text-ink">{date?.format("ddd, D MMM")} · {slot?.time || slot} IST</dd></div>
                      <div className="flex justify-between"><dt className="text-faint">Mode</dt><dd className="font-medium text-ink">{chosen?.mode}</dd></div>
                      <div className="flex justify-between"><dt className="text-faint">Fee</dt><dd className="font-medium text-ink">{chosen?.price}</dd></div>
                      <div className="flex justify-between"><dt className="text-faint">Name</dt><dd className="font-medium text-ink">{form.name}</dd></div>
                      <div className="flex justify-between"><dt className="text-faint">WhatsApp</dt><dd className="font-medium text-ink">{form.phone}</dd></div>
                    </dl>
                    {paymentOptions.length > 0 && (
                      <div className="mt-5" data-testid="booking-payment-options">
                        <p className="mb-2 font-outfit text-xs font-semibold uppercase tracking-widest text-faint">Payment method</p>
                        <div className="space-y-2">
                          {paymentOptions.map((o) => (
                            <button
                              key={o.id}
                              data-testid={`booking-pay-${o.id}`}
                              onClick={() => setPayMethod(o.id)}
                              className={`w-full rounded-xl border px-4 py-3 text-left transition-all duration-200 ${
                                payMethod === o.id ? "border-terra bg-terra/5" : "border-ink/10 bg-sand hover:border-terra/40"
                              }`}
                            >
                              <p className="font-outfit text-sm font-semibold text-ink">{o.label}</p>
                              <p className="text-xs text-faint">{o.sub}</p>
                            </button>
                          ))}
                        </div>
                        {payMethod === "direct" && (
                          <div className="mt-3 rounded-xl bg-sagelight p-4 text-xs leading-relaxed text-ink" data-testid="booking-direct-details">
                            {directDetails.upi_id && <p>UPI ID: <span className="font-semibold">{directDetails.upi_id}</span></p>}
                            {directDetails.qr_code_url && (
                              <img src={directDetails.qr_code_url} alt="UPI QR code" className="mt-2 h-28 w-28 rounded-lg border border-ink/10 bg-white object-contain p-1" />
                            )}
                            {directDetails.instructions && <p className="mt-2 text-plumsoft">{directDetails.instructions}</p>}
                          </div>
                        )}
                      </div>
                    )}
                    <p className="mt-4 rounded-xl bg-sagelight px-4 py-3 text-xs leading-relaxed text-sage">
                      You'll receive an instant confirmation with reminders on WhatsApp & email.
                    </p>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {!done && (
          <div className="flex shrink-0 items-center justify-between border-t border-ink/10 px-7 py-4">
            <button
              data-testid="booking-back-btn"
              onClick={() => setStep((s) => Math.max(0, s - 1))}
              disabled={step === 0}
              className="inline-flex items-center gap-1.5 font-outfit text-sm font-medium text-faint transition-colors enabled:hover:text-ink disabled:opacity-40"
            >
              <ArrowLeft className="h-4 w-4" /> Back
            </button>
            {step < 3 ? (
              <button
                data-testid="booking-next-btn"
                onClick={() => {
                  if (!canNext) return;
                  if (step === 1 && live) loadIntake();
                  setStep((s) => s + 1);
                }}
                disabled={!canNext}
                className="inline-flex items-center gap-1.5 rounded-full bg-ink px-6 py-2.5 font-outfit text-sm font-semibold text-sand transition-colors duration-300 enabled:hover:bg-terra disabled:opacity-40"
              >
                Continue <ArrowRight className="h-4 w-4" />
              </button>
            ) : (
              <button
                data-testid="booking-confirm-btn"
                onClick={submit}
                disabled={submitting || (live && paymentOptions.length > 0 && !payMethod)}
                className="inline-flex items-center gap-2 rounded-full bg-terra px-6 py-2.5 font-outfit text-sm font-semibold text-white transition-colors duration-300 hover:bg-terradark disabled:opacity-60"
              >
                {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                {live ? "Confirm booking" : "Request booking"}
              </button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
